/* ===================================================================
   KAAYU - PDP MOTION

   Reveals the product page's sections as they enter the viewport, and staggers
   the cards inside them a beat behind their heading.

   THE SAFETY ORDER MATTERS
   assets/kaayu-pdp-motion.css hides nothing on its own: every rule that sets
   opacity:0 is behind `html.kx-armed`. This file adds that class only after it
   has already installed the IntersectionObserver that will remove it again, and
   only when the reader has not asked for reduced motion. So the failure modes
   are all the same failure mode - the page renders normally, unanimated.

   The safety net is a proof check, not a blanket timer. An earlier version
   revealed everything after three seconds no matter what, which guaranteed
   nothing could stay hidden but also meant a reader who took longer than three
   seconds to scroll saw no animation at all below the fold. Instead: after two
   and a half seconds, if the observer has not fired even once, it is presumed
   broken and everything is revealed. If it has fired, it works, and it is left
   to do its job for the whole page.

   WHAT IS SKIPPED, AND WHY
   - .product-media and its ancestors: the gallery is position:sticky, and a
     transform on an ancestor becomes its containing block and breaks it
   - the buy column: price, pack tiles and buy buttons are above the fold and
     must never be mid-fade when the page settles
   - .kaayu-timeline and .kaayu-prodinfo: both already run their own reveal,
     and two observers on one section fight over the same opacity
   =================================================================== */
(function () {
  'use strict';

  var ARMED = 'kx-armed';
  var OBSERVER_CHECK = 2500; // ms; how long to wait for proof the observer runs
  var STAGGER_STEP = 70;     // ms between siblings
  var STAGGER_CAP  = 420;    // ms; nothing waits longer than this

  /* Sections that own their motion already. Matched with closest(). */
  var SELF_ANIMATED = '.kaayu-timeline, .kaayu-prodinfo';

  /* Section roots to reveal, in the order they appear on the page. Each is a
     Kaayu-owned block; theme sections are left alone. */
  var SECTIONS = [
    '.kaayu-claims-strip',
    '.kaayu-comparison',
    '[id*="kaayu_related"] .shopify-section, [id*="kaayu_related"]',
    '.kaayu-ingredients-section',
    'section.kh-band',
    '.kfaq-band'
  ].join(', ');

  /* Children worth staggering inside a revealed section. */
  var CHILDREN = [
    '.kcmp__card',
    '.kfaq-item',
    '.ki-card',
    '.product-card'
  ].join(', ');

  function disarm() {
    document.documentElement.classList.remove(ARMED);
  }

  function start() {
    var still = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (still.matches || !('IntersectionObserver' in window)) return;

    var roots = [];
    try {
      roots = Array.prototype.slice.call(document.querySelectorAll(SECTIONS));
    } catch (e) {
      return;   // a selector this browser cannot parse: leave the page alone
    }

    roots = roots.filter(function (el) {
      if (el.closest(SELF_ANIMATED)) return false;
      // never put a transform between a sticky element and its scrollport
      if (el.querySelector('.product-media')) return false;
      if (el.closest('.product-info-inner')) return false;
      return true;
    });

    if (!roots.length) return;

    var everFired = false;

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        everFired = true;
        var root = entry.target;
        root.classList.add('kx-in');

        var kids = root.querySelectorAll(CHILDREN);
        for (var i = 0; i < kids.length; i++) {
          kids[i].classList.add('kx-in');
        }
        io.unobserve(root);
      });
    }, { threshold: 0.06, rootMargin: '0px 0px -5% 0px' });

    roots.forEach(function (el) {
      el.classList.add('kx-reveal');

      var kids = el.querySelectorAll(CHILDREN);
      for (var i = 0; i < kids.length; i++) {
        kids[i].classList.add('kx-stagger');
        kids[i].style.setProperty(
          '--kx-delay',
          Math.min(i * STAGGER_STEP, STAGGER_CAP) + 'ms'
        );
      }

      io.observe(el);
    });

    /* Only now is it safe to hide anything: the observer that will reveal
       these sections is already watching them. */
    document.documentElement.classList.add(ARMED);

    /* The first section is at the top of the page, so a working observer fires
       almost immediately. Silence by now means it is not going to fire, and a
       section that never reveals is worse than one that never animates. */
    window.setTimeout(function () {
      if (everFired) return;
      roots.forEach(function (el) {
        el.classList.add('kx-in');
        var kids = el.querySelectorAll(CHILDREN);
        for (var i = 0; i < kids.length; i++) kids[i].classList.add('kx-in');
      });
      disarm();
    }, OBSERVER_CHECK);

    /* A tab restored from the back/forward cache re-runs nothing, so a page
       left mid-scroll could come back with sections still hidden. */
    window.addEventListener('pageshow', function (e) {
      if (!e.persisted) return;
      roots.forEach(function (el) {
        var r = el.getBoundingClientRect();
        if (r.top < window.innerHeight && r.bottom > 0) el.classList.add('kx-in');
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
